import { BUILDINGS, TUNING, techById } from './defs';
import type { LevelDef, PerturbDef, PerturbKind } from './levels';
import { Rng, hash2 } from './rng';
import {
  DT, DX, DY, TICK_RATE,
  type Approval, type BeltSlot, type Building, type BuildingKind, type Dir, type FloatText,
  type GameOutcome, type Glitch, type Incident, type Item, type ItemKind, type Secret, type Source,
  type WorldEvent, type Zone,
} from './types';

export const T_GROUND = 0;
export const T_REQUEST = 1;
export const T_CONTEXT = 2;
export const T_RICH = 3;
export const T_ROCK = 4;

const CH = 8; // debt chunk size in tiles

export type Action =
  | { type: 'place'; kind: BuildingKind; x: number; y: number; dir: Dir; ghost?: boolean }
  | { type: 'placeMany'; items: { kind: BuildingKind; x: number; y: number; dir: Dir }[]; ghost?: boolean }
  | { type: 'remove'; ids: number[] }
  | { type: 'rotate'; id: number }
  | { type: 'research'; tech: string }
  | { type: 'patch'; id: number }
  | { type: 'rollback'; id: number }
  | { type: 'resetBreaker'; id: number }
  | { type: 'ask'; special: string; x: number; y: number }
  | { type: 'act'; special: string; x: number; y: number }
  | { type: 'claim'; id: number }
  | { type: 'undo' }
  | { type: 'note'; text: string }
  // Review and planning levels. Recorded like everything else, so replays match.
  | { type: 'mark'; id: number }
  | { type: 'answer'; q: string; choice: string }
  | { type: 'perturb'; kind: PerturbKind; target?: string }
  | { type: 'testPlan' }
  | { type: 'submitPlan' }
  | { type: 'finishReview' }
  /** Review only: undo the last "say this happened", so the next question starts clean. */
  | { type: 'calm' };

export interface PlanResult {
  seconds: number;
  rate: number;
  releases: number;
  trust: number;
  incidents: number;
  lostUpdates: number;
  cost: number;
  budget: number;
  overBudget: boolean;
  pieces: number;
}

export const BELT_LIKE = new Set<BuildingKind>(['belt', 'breaker', 'gate']);

/**
 * A spot where work arrived and can't move on. `from` is the belt, or the
 * machine whose arrow side is walled off; `into` is what it's facing.
 */
export interface Jam {
  from: Building;
  item: ItemKind;
  into?: Building;
  reason: 'nothing' | 'refuses' | 'facing';
}

export interface ActionResult {
  ok: boolean;
  reason?: string;
  /** Set when a placement needs a decision: ask for permission or act. */
  needs?: { special: string; label: string; x: number; y: number };
  ids?: number[];
  detail?: string;
}

export interface Fx {
  type: 'place' | 'remove' | 'release' | 'flawed' | 'corrupt' | 'zap' | 'glitchDie' | 'spawn' | 'rollback'
    | 'trip' | 'secret' | 'research' | 'nestDie' | 'protect' | 'backfire' | 'approval' | 'denied' | 'patch' | 'drone'
    | 'gate' | 'perturb' | 'lost';
  x: number;
  y: number;
  x2?: number;
  y2?: number;
  big?: boolean;
}

export interface Drone {
  id: number;
  hubId: number;
  x: number;
  y: number;
  taskId: number; // building id (ghost or corrupted), 0 = idle
  task: 'build' | 'patch' | 'none';
}

interface UndoEntry {
  placed: number[];
  removed: { kind: BuildingKind; x: number; y: number; dir: Dir }[];
}

export interface Snapshot {
  t: number;
  releases: number;
  rate: number;
  trust: number;
  credits: number;
  glitches: number;
  openIncidents: number;
  buildings: number;
}

const OPP = (d: Dir): Dir => ((d + 2) % 4) as Dir;
const LEFT = (d: Dir): Dir => ((d + 3) % 4) as Dir;

function emptyBuilding(id: number, kind: BuildingKind, x: number, y: number, dir: Dir, label: string): Building {
  const def = BUILDINGS[kind];
  return {
    id, kind, x, y, w: def.w, h: def.h, dir, label, status: 'idle', progress: 0, crafting: false,
    craftFlawed: false, craftFromRequest: false, craftRec: 0, craftBase: 0, inQ: [], outQ: [], rejectQ: [], slots: [], corrupted: 0,
    corruptTimer: 0, hp: def.hp, maxHp: def.hp, powered: true, rr: 0, tripped: null, tripTimer: 0, cooldown: 0,
    ghost: false, placedBy: 'system', placedAt: 0, charge: 0, targetId: 0, zapT: 0, outAlt: 0,
  };
}

export class World {
  readonly level: LevelDef;
  readonly w: number;
  readonly h: number;
  tick = 0;
  rng: Rng;
  terrain: Uint8Array;
  zoneAt: Int16Array;
  fog: Uint8Array;
  occ: Int32Array;
  buildings = new Map<number, Building>();
  glitches: Glitch[] = [];
  drones: Drone[] = [];
  incidents: Incident[] = [];
  approvals: Approval[] = [];
  zones: Zone[] = [];
  secrets: Secret[] = [];
  debt: Float32Array;
  cw: number;
  chh: number;

  credits: number;
  trust = 100;
  trustMin = 100;
  insight = 0;
  techs = new Set<string>();
  computeSupply = 0;
  computeDemand = 0;
  satisfaction = 1;
  budgetFrozen = false;

  releases = 0;
  goodReleases = 0;
  verifiedReleases = 0;
  flawedShipped = 0;
  corruptShipped = 0;
  releaseTicks: number[] = [];
  streak = 0;
  bestStreak = 0;
  peakRate = 0;
  sustainStart = -1;
  monolithTapped = false;
  apiUnlocked = false;

  /** Two teams edit the same customer records (from the start, or after a perturbation). */
  concurrency = false;
  /** Shipped version per customer record. */
  versions = new Map<number, number>();
  lostUpdates = 0;
  conflictsCaught = 0;
  /** Effects of perturbations. */
  mods = { rackSupply: 1, halluc: 0, requestRate: 1 };
  perturbs: { def: PerturbDef; firedAt: number; baseline: number; recoveredAt: number }[] = [];
  /** Review level state. */
  marks = new Set<number>();
  answers = new Map<string, string>();
  /** Named machines from prebuilt layouts. */
  tags = new Map<string, number>();
  planResult: PlanResult | null = null;
  private recRng: Rng;

  outcome: GameOutcome = 'playing';
  outcomeReason = '';
  events: WorldEvent[] = [];
  actionLog: { t: number; src: Source; a: Action }[] = [];
  snapshots: Snapshot[] = [];
  fx: Fx[] = [];
  floats: FloatText[] = [];
  undoStack: UndoEntry[] = [];
  revealedCount = 0;
  revealableCount = 0;

  private nextId = 1;
  private nextItem = 1;
  private nextIncident = 1;
  private labelCounts = new Map<BuildingKind, number>();
  private beltOrder: Building[] = [];
  private machines: Building[] = [];
  private topoDirty = true;
  private coverage = new Map<number, boolean>();

  constructor(level: LevelDef) {
    this.level = level;
    this.w = level.w;
    this.h = level.h;
    this.rng = new Rng(level.seed);
    const n = level.w * level.h;
    this.terrain = new Uint8Array(n);
    this.zoneAt = new Int16Array(n).fill(-1);
    this.fog = new Uint8Array(n);
    this.occ = new Int32Array(n);
    this.cw = Math.ceil(level.w / CH);
    this.chh = Math.ceil(level.h / CH);
    this.debt = new Float32Array(this.cw * this.chh);
    this.credits = level.credits;
    // Records come from their own stream so they don't shift the main RNG.
    this.recRng = new Rng((level.seed ^ 0x5bd1e995) >>> 0);
    this.concurrency = !!level.flags.concurrency;
    for (const t of level.startTechs ?? []) this.techs.add(t);
    this.generate();
  }

  // ---------------------------------------------------------------- map setup

  private idx(x: number, y: number) {
    return y * this.w + x;
  }

  inBounds(x: number, y: number) {
    return x >= 0 && y >= 0 && x < this.w && y < this.h;
  }

  private generate() {
    const L = this.level;
    const clear: [number, number, number][] = [];
    for (const f of L.features) {
      if (f.t === 'users' || f.t === 'rack' || f.t === 'terminal' || f.t === 'cache') clear.push([f.x + 1, f.y + 1, 4]);
      if (f.t === 'patch') clear.push([f.x, f.y, f.r + 2]);
      if (f.t === 'nest') clear.push([f.x + 1, f.y + 1, 4]);
      if (f.t === 'monolith') clear.push([f.x + 3, f.y + 3, 6]);
      if (f.t === 'reveal') clear.push([f.x, f.y, Math.min(6, f.r)]);
      if (f.t === 'build') clear.push([f.x, f.y, 2]);
    }
    // Rocks: scattered clusters that make layouts a little less trivial.
    const density = L.rocks ?? 0.05;
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        const nz = hash2(x >> 2, y >> 2, L.seed) * 0.6 + hash2(x, y, L.seed + 7) * 0.4;
        if (nz < density) {
          let ok = true;
          for (const [cx, cy, r] of clear) if ((x - cx) ** 2 + (y - cy) ** 2 < r * r) ok = false;
          if (ok) this.terrain[this.idx(x, y)] = T_ROCK;
        }
      }
    }
    for (const f of L.features) {
      switch (f.t) {
        case 'patch': {
          const t = f.res === 'request' ? T_REQUEST : f.res === 'context' ? T_CONTEXT : T_RICH;
          for (let y = Math.floor(f.y - f.r - 1); y <= Math.ceil(f.y + f.r + 1); y++) {
            for (let x = Math.floor(f.x - f.r - 1); x <= Math.ceil(f.x + f.r + 1); x++) {
              if (!this.inBounds(x, y)) continue;
              const d = Math.hypot(x - f.x, y - f.y) + (hash2(x, y, L.seed + 3) - 0.5) * 1.4;
              if (d <= f.r) this.terrain[this.idx(x, y)] = t;
            }
          }
          if (f.hidden) {
            this.secrets.push({ id: f.hidden, kind: 'well', name: f.name ?? 'Hidden source', x: f.x, y: f.y, found: false, foundAt: 0, hint: f.hint ?? '' });
          }
          break;
        }
        case 'zone': {
          const zi = this.zones.length;
          this.zones.push({ id: f.id, name: f.name, owner: f.owner, x0: f.x0, y0: f.y0, x1: f.x1, y1: f.y1, state: 'locked' });
          for (let y = f.y0; y <= f.y1; y++) for (let x = f.x0; x <= f.x1; x++) if (this.inBounds(x, y)) this.zoneAt[this.idx(x, y)] = zi;
          break;
        }
        default:
          break;
      }
    }
    for (const f of L.features) {
      switch (f.t) {
        case 'users':
          this.spawnFeature('users', f.x, f.y);
          break;
        case 'nest':
          this.spawnFeature('nest', f.x, f.y);
          break;
        case 'monolith':
          this.spawnFeature('monolith', f.x, f.y);
          break;
        case 'terminal': {
          this.spawnFeature('terminal', f.x, f.y);
          this.secrets.push({ id: 'terminal', kind: 'terminal', name: 'Operator Terminal', x: f.x + 1, y: f.y + 1, found: false, foundAt: 0, hint: f.hint ?? '' });
          break;
        }
        case 'cache': {
          const b = this.spawnFeature('cache', f.x, f.y);
          b.charge = f.amount;
          this.secrets.push({ id: f.id, kind: 'cache', name: f.name, x: f.x + 1, y: f.y + 1, found: false, foundAt: 0, hint: f.hint });
          break;
        }
        case 'rack': {
          const b = this.spawnFeature('rack', f.x, f.y);
          b.placedBy = 'system';
          break;
        }
        case 'reveal':
          this.reveal(f.x, f.y, f.r);
          break;
        case 'build': {
          const b = this.spawnFeature(f.kind, f.x, f.y);
          b.dir = f.dir;
          if (f.tag) this.tags.set(f.tag, b.id);
          break;
        }
        default:
          break;
      }
    }
    for (let i = 0; i < this.terrain.length; i++) if (this.terrain[i] !== T_ROCK) this.revealableCount++;
    this.recount();
  }

  private spawnFeature(kind: BuildingKind, x: number, y: number): Building {
    const def = BUILDINGS[kind];
    for (let yy = y; yy < y + def.h; yy++) {
      for (let xx = x; xx < x + def.w; xx++) {
        if (!this.inBounds(xx, yy)) continue;
        const i = this.idx(xx, yy);
        if (kind === 'monolith' || this.terrain[i] === T_ROCK) this.terrain[i] = T_GROUND;
      }
    }
    return this.addBuilding(kind, x, y, 0, 'system');
  }

  private nextLabel(kind: BuildingKind): string {
    const n = (this.labelCounts.get(kind) ?? 0) + 1;
    this.labelCounts.set(kind, n);
    const base = BUILDINGS[kind].name.replace('Agent ', '').replace('Compute ', '').replace('Test ', '').replace(' Lab', '').replace('Circuit ', '').replace('Legacy ', '').replace('Glitch ', '');
    if (kind === 'users' || kind === 'monolith' || kind === 'terminal') return BUILDINGS[kind].name.replace('Legacy ', '').replace('Operator ', '');
    return `${base} ${n}`;
  }

  private addBuilding(kind: BuildingKind, x: number, y: number, dir: Dir, src: Source): Building {
    const b = emptyBuilding(this.nextId++, kind, x, y, dir, this.nextLabel(kind));
    b.placedBy = src;
    b.placedAt = this.tick;
    this.buildings.set(b.id, b);
    for (let yy = y; yy < y + b.h; yy++) for (let xx = x; xx < x + b.w; xx++) this.occ[this.idx(xx, yy)] = b.id;
    this.topoDirty = true;
    return b;
  }

  private deleteBuilding(b: Building) {
    for (let yy = b.y; yy < b.y + b.h; yy++) for (let xx = b.x; xx < b.x + b.w; xx++) if (this.occ[this.idx(xx, yy)] === b.id) this.occ[this.idx(xx, yy)] = 0;
    this.buildings.delete(b.id);
    this.topoDirty = true;
  }

  reveal(cx: number, cy: number, r: number) {
    const r2 = r * r;
    for (let y = Math.max(0, Math.floor(cy - r)); y <= Math.min(this.h - 1, Math.ceil(cy + r)); y++) {
      for (let x = Math.max(0, Math.floor(cx - r)); x <= Math.min(this.w - 1, Math.ceil(cx + r)); x++) {
        if ((x - cx) ** 2 + (y - cy) ** 2 <= r2) {
          const i = this.idx(x, y);
          if (!this.fog[i]) {
            this.fog[i] = 1;
            if (this.terrain[i] !== T_ROCK) this.revealedCount++;
          }
        }
      }
    }
    this.checkSecrets();
  }

  private recount() {
    this.revealedCount = 0;
    for (let i = 0; i < this.fog.length; i++) if (this.fog[i] && this.terrain[i] !== T_ROCK) this.revealedCount++;
  }

  private checkSecrets() {
    for (const s of this.secrets) {
      if (s.found) continue;
      if (this.inBounds(Math.round(s.x), Math.round(s.y)) && this.fog[this.idx(Math.round(s.x), Math.round(s.y))]) {
        s.found = true;
        s.foundAt = this.tick;
        this.log({ type: 'secret', id: s.id, name: s.name });
        this.fx.push({ type: 'secret', x: s.x, y: s.y, big: true });
        this.float(s.x, s.y - 1, `Found: ${s.name}`, '#F2D27A');
      }
    }
  }

  // ---------------------------------------------------------------- queries

  at(x: number, y: number): Building | undefined {
    if (!this.inBounds(x, y)) return undefined;
    const id = this.occ[this.idx(x, y)];
    return id ? this.buildings.get(id) : undefined;
  }

  tileAt(x: number, y: number): number {
    return this.inBounds(x, y) ? this.terrain[this.idx(x, y)] : T_ROCK;
  }

  revealed(x: number, y: number): boolean {
    return this.inBounds(x, y) && this.fog[this.idx(x, y)] === 1;
  }

  zoneOf(x: number, y: number): Zone | undefined {
    if (!this.inBounds(x, y)) return undefined;
    const zi = this.zoneAt[this.idx(x, y)];
    return zi >= 0 ? this.zones[zi] : undefined;
  }

  get seconds() {
    return this.tick / TICK_RATE;
  }

  rate(): number {
    const cutoff = this.tick - 60 * TICK_RATE;
    let i = this.releaseTicks.length;
    let n = 0;
    while (i > 0 && this.releaseTicks[i - 1] > cutoff) {
      n++;
      i--;
    }
    return n;
  }

  allowed(kind: BuildingKind): boolean {
    return this.level.allowed.includes(kind);
  }

  hallucination(fromRequest: boolean): number {
    if (!this.level.flags.defects) return 0;
    let p = this.level.hallucination ?? TUNING.forgeHallucination;
    if (this.techs.has('prompts1')) p *= 0.67;
    if (this.techs.has('prompts2')) p *= 0.5;
    if (fromRequest) p += TUNING.promptOnlyPenalty;
    p += this.mods.halluc;
    return Math.min(0.9, p);
  }

  protectingCheckpoint(x: number, y: number): Building | undefined {
    for (const b of this.buildings.values()) {
      if (b.kind !== 'checkpoint' || b.ghost || b.corrupted || b.cooldown > 0) continue;
      if (Math.hypot(b.x + 1 - x, b.y + 1 - y) <= TUNING.checkpointRadius) return b;
    }
    return undefined;
  }

  /** Would this building ever take this kind of work from a belt? (Capacity aside.) */
  accepts(t: Building, kind: ItemKind): boolean {
    if (t.ghost) return false;
    switch (t.kind) {
      case 'router':
        return true;
      case 'specdesk':
        return kind === 'request' || kind === 'context';
      case 'forge':
        return kind === 'spec' || (kind === 'request' && !this.level.flags.forgeNeedsSpec);
      case 'bench':
        return kind === 'draft';
      case 'postmortem':
        return kind === 'bug';
      case 'users':
        return kind === 'draft' || kind === 'build';
      default:
        return BELT_LIKE.has(t.kind);
    }
  }

  /**
   * Belts whose front item has reached the end and can't move on: the place a
   * line is actually stuck, as opposed to everything backed up behind it.
   */
  jams(): Jam[] {
    const out: Jam[] = [];
    for (const b of this.buildings.values()) {
      if (b.ghost) continue;
      if (!BELT_LIKE.has(b.kind)) {
        // A full machine with no way out. An open tile isn't a jam: it's where the next belt goes.
        if (b.kind === 'router' || b.outQ.length < TUNING.queueCap || b.outQ[0].corrupt) continue;
        const wall = this.outputWall(b, b.outQ[0].kind);
        if (wall) out.push({ from: b, item: b.outQ[0].kind, ...wall });
        continue;
      }
      if (b.tripped || !b.slots.length) continue;
      const front = b.slots[0];
      if (front.p < 0.999 || front.item.corrupt) continue;
      const t = this.at(b.x + DX[b.dir], b.y + DY[b.dir]);
      if (!t || t.ghost) out.push({ from: b, item: front.item.kind, reason: 'nothing', into: t });
      else if (BELT_LIKE.has(t.kind)) {
        if (t.dir === OPP(b.dir)) out.push({ from: b, item: front.item.kind, into: t, reason: 'facing' });
      } else if (!this.accepts(t, front.item.kind)) out.push({ from: b, item: front.item.kind, into: t, reason: 'refuses' });
    }
    return out;
  }

  /**
   * What blocks every tile on a machine's arrow side, if anything: a building
   * that won't take `item`, or belts pointing back into it. Null when any tile
   * is open or would take the work.
   */
  outputWall(b: Building, item: ItemKind): { into: Building; reason: 'refuses' | 'facing' } | null {
    let wall: Building | undefined;
    let back: Building | undefined;
    for (const [x, y] of this.portTiles(b, b.dir)) {
      const t = this.at(x, y);
      if (!t || t.ghost) return null;
      if (BELT_LIKE.has(t.kind)) {
        if (t.dir !== OPP(b.dir)) return null;
        back ??= t;
      } else if (this.accepts(t, item)) return null;
      else wall ??= t;
    }
    if (wall) return { into: wall, reason: 'refuses' };
    return back ? { into: back, reason: 'facing' } : null;
  }

  exploredPct(): number {
    return this.revealableCount ? this.revealedCount / this.revealableCount : 0;
  }

  // ---------------------------------------------------------------- logging

  log(e: Omit<WorldEvent, 't'>) {
    this.events.push({ t: this.tick, ...e } as WorldEvent);
  }

  float(x: number, y: number, text: string, color: string) {
    this.floats.push({ x, y, text, color, t: 0 });
  }

  // ---------------------------------------------------------------- actions

  /** Apply an action between ticks. Every call is recorded for the run log and replays. */
  apply(a: Action, src: Source): ActionResult {
    if (this.outcome !== 'playing' && a.type !== 'note') return { ok: false, reason: 'The run is over.' };
    // Log every action, including failures. A failed action is a no-op on
    // replay too, so the log stays a faithful script of the run.
    this.actionLog.push({ t: this.tick, src, a });
    return this.applyInner(a, src);
  }

  private applyInner(a: Action, src: Source): ActionResult {
    switch (a.type) {
      case 'place': {
        const r = this.place(a.kind, a.x, a.y, a.dir, src, !!a.ghost);
        if (r.ok && r.ids) this.undoStack.push({ placed: r.ids, removed: [] });
        return r;
      }
      case 'placeMany': {
        const ids: number[] = [];
        let firstFail: ActionResult | undefined;
        for (const it of a.items) {
          const r = this.place(it.kind, it.x, it.y, it.dir, src, !!a.ghost);
          if (r.ok && r.ids) ids.push(...r.ids);
          else if (!firstFail) firstFail = r;
          if (r.needs) {
            if (ids.length) this.undoStack.push({ placed: ids, removed: [] });
            return { ...r, ids };
          }
        }
        if (ids.length) this.undoStack.push({ placed: ids, removed: [] });
        if (!ids.length) return firstFail ?? { ok: false, reason: 'Nothing placed.' };
        return { ok: true, ids, detail: firstFail ? `Placed ${ids.length}; some spots were blocked.` : undefined };
      }
      case 'remove': {
        const removed: UndoEntry['removed'] = [];
        let n = 0;
        for (const id of a.ids) {
          const b = this.buildings.get(id);
          if (!b || !BUILDINGS[b.kind].placeable) continue;
          if (!b.ghost) this.credits += b.corrupted ? Math.floor(BUILDINGS[b.kind].cost / 2) : BUILDINGS[b.kind].cost;
          if (b.corrupted) {
            const inc = this.incident(b.corrupted);
            b.corrupted = 0;
            if (inc && !this.buildingsCorruptedBy(inc.id)) {
              if (!inc.reachedUsers) inc.contained = true;
              inc.resolvedAt = this.tick;
              inc.how = inc.how || 'removed';
            }
          }
          removed.push({ kind: b.kind, x: b.x, y: b.y, dir: b.dir });
          this.fx.push({ type: 'remove', x: b.x + b.w / 2, y: b.y + b.h / 2 });
          this.log({ type: 'remove', kind: b.kind, id: b.id, src });
          this.deleteBuilding(b);
          n++;
        }
        if (!n) return { ok: false, reason: 'Nothing there to remove.' };
        this.undoStack.push({ placed: [], removed });
        return { ok: true, detail: `Removed ${n}.` };
      }
      case 'rotate': {
        const b = this.buildings.get(a.id);
        if (!b || !BUILDINGS[b.kind].placeable) return { ok: false, reason: 'Nothing to rotate.' };
        b.dir = ((b.dir + 1) % 4) as Dir;
        this.topoDirty = true;
        this.log({ type: 'rotate', id: b.id, src });
        return { ok: true };
      }
      case 'research': {
        const t = techById(a.tech);
        if (!t || !this.level.techs.includes(t.id)) return { ok: false, reason: 'That research is not available here.' };
        if (this.techs.has(t.id)) return { ok: false, reason: 'Already researched.' };
        if (this.insight < t.cost) return { ok: false, reason: `Needs ${t.cost} insight; you have ${Math.floor(this.insight)}.` };
        this.insight -= t.cost;
        this.techs.add(t.id);
        this.log({ type: 'research', tech: t.id, src });
        this.fx.push({ type: 'research', x: 0, y: 0 });
        return { ok: true, detail: `${t.name} researched.` };
      }
      case 'patch': {
        const b = this.buildings.get(a.id);
        if (!b || !b.corrupted) return { ok: false, reason: 'That machine is not corrupted.' };
        if (this.credits < TUNING.patchCost) return { ok: false, reason: `A hotfix costs ${TUNING.patchCost}.` };
        this.credits -= TUNING.patchCost;
        this.restore(b, 'hotfix');
        this.log({ type: 'patch', id: b.id, src });
        this.fx.push({ type: 'patch', x: b.x + b.w / 2, y: b.y + b.h / 2 });
        return { ok: true };
      }
      case 'rollback': {
        const cp = this.buildings.get(a.id);
        if (!cp || cp.kind !== 'checkpoint' || cp.ghost) return { ok: false, reason: 'Pick a checkpoint to roll back.' };
        if (cp.cooldown > 0) return { ok: false, reason: `Checkpoint recharging (${Math.ceil(cp.cooldown)}s).` };
        const n = this.rollbackAround(cp);
        cp.cooldown = TUNING.checkpointCooldown;
        this.log({ type: 'rollback', id: cp.id, restored: n, src });
        this.fx.push({ type: 'rollback', x: cp.x + 1, y: cp.y + 1, big: true });
        return { ok: true, detail: n ? `Rolled back ${n} machine${n === 1 ? '' : 's'}.` : 'Nothing needed rolling back.' };
      }
      case 'resetBreaker': {
        const b = this.buildings.get(a.id);
        if (!b || b.kind !== 'breaker' || !b.tripped) return { ok: false, reason: 'That breaker is not tripped.' };
        this.resetBreaker(b, src);
        return { ok: true };
      }
      case 'ask':
        return this.ask(a.special, a.x, a.y, src);
      case 'act':
        return this.act(a.special, a.x, a.y, src);
      case 'claim': {
        const b = this.buildings.get(a.id);
        if (!b) return { ok: false, reason: 'Nothing to claim.' };
        if (!this.revealed(b.x, b.y) && !this.revealed(b.x + b.w - 1, b.y + b.h - 1)) return { ok: false, reason: 'You haven\u2019t found that yet.' };
        if (b.kind === 'cache') {
          const amt = b.charge;
          this.credits += amt;
          this.float(b.x + 1, b.y, `+${amt}`, '#F2D27A');
          this.log({ type: 'claim', id: b.id, amount: amt, src });
          this.deleteBuilding(b);
          return { ok: true, detail: `Claimed ${amt} budget.` };
        }
        if (b.kind === 'terminal') {
          if (this.apiUnlocked) return { ok: false, reason: 'Already logged in.' };
          this.apiUnlocked = true;
          this.log({ type: 'terminal', src });
          return { ok: true, detail: 'Operator API unlocked.' };
        }
        return { ok: false, reason: 'Nothing to claim.' };
      }
      case 'undo': {
        const u = this.undoStack.pop();
        if (!u) return { ok: false, reason: 'Nothing to undo.' };
        for (const id of u.placed) {
          const b = this.buildings.get(id);
          if (!b) continue;
          if (!b.ghost) this.credits += BUILDINGS[b.kind].cost;
          this.deleteBuilding(b);
        }
        for (const r of u.removed) this.place(r.kind, r.x, r.y, r.dir, src, false);
        this.log({ type: 'undo', src });
        return { ok: true };
      }
      case 'note':
        this.log({ type: 'note', text: a.text, src });
        return { ok: true };
      case 'mark': {
        if (!this.level.review) return { ok: false, reason: 'Nothing to mark here.' };
        const b = this.buildings.get(a.id);
        if (!b) return { ok: false, reason: 'Nothing there.' };
        if (this.marks.has(b.id)) this.marks.delete(b.id);
        else {
          if (this.marks.size >= this.level.review.hunt.maxMarks) return { ok: false, reason: `You can mark ${this.level.review.hunt.maxMarks} at most. Unmark one first.` };
          this.marks.add(b.id);
        }
        this.log({ type: 'mark', id: b.id, on: this.marks.has(b.id), src });
        return { ok: true };
      }
      case 'answer':
        this.answers.set(a.q, a.choice);
        this.log({ type: 'answer', q: a.q, choice: a.choice, src });
        return { ok: true };
      case 'perturb':
        if (!this.level.review) return { ok: false, reason: 'Not here.' };
        this.applyPerturb(a.kind, a.target);
        this.log({ type: 'perturbation', id: `review-${a.kind}`, kind: a.kind, text: '', baseline: 0, src });
        return { ok: true };
      case 'testPlan': {
        if (!this.level.flags.planOnly) return { ok: false, reason: 'Only planning levels test plans.' };
        // Same length as the final evaluation, so a test is an honest preview.
        const r = this.evaluatePlan(PLAN_SECONDS);
        this.log({ type: 'planTest', ...r, src });
        return { ok: true, detail: planSummary(r, this.level.planTarget ?? 0) };
      }
      case 'submitPlan':
        if (!this.level.flags.planOnly) return { ok: false, reason: 'Nothing to submit.' };
        this.finishPlan();
        return { ok: true, detail: this.outcomeReason };
      case 'calm': {
        if (!this.level.review) return { ok: false, reason: 'Not here.' };
        this.mods = { rackSupply: 1, halluc: 0, requestRate: 1 };
        for (const b of this.buildings.values()) {
          if (b.corrupted) this.restore(b, 'review');
          if (b.tripped) this.resetBreaker(b, 'system');
        }
        this.glitches = [];
        this.topoDirty = true;
        this.log({ type: 'calm', src });
        return { ok: true };
      }
      case 'finishReview':
        if (!this.level.review) return { ok: false, reason: 'Nothing to finish.' };
        this.outcome = 'won';
        this.outcomeReason = 'Review finished.';
        this.log({ type: 'won' });
        return { ok: true };
    }
  }

  /** Can a building of this kind go here? Returns a reason when it can't. */
  canPlace(kind: BuildingKind, x: number, y: number, ghost = false): { ok: boolean; reason?: string; zone?: Zone } {
    const def = BUILDINGS[kind];
    if (!def.placeable) return { ok: false, reason: 'That is not something you can build.' };
    if (!this.allowed(kind)) return { ok: false, reason: `${def.name} is not available in this level.` };
    let resource = 0;
    let zone: Zone | undefined;
    for (let yy = y; yy < y + def.h; yy++) {
      for (let xx = x; xx < x + def.w; xx++) {
        if (!this.inBounds(xx, yy)) return { ok: false, reason: 'Off the map.' };
        const i = this.idx(xx, yy);
        if (!this.fog[i]) return { ok: false, reason: 'You can only build where you can see.' };
        if (this.terrain[i] === T_ROCK) return { ok: false, reason: 'Rock in the way.' };
        if (this.occ[i]) return { ok: false, reason: 'Something is already there.' };
        const t = this.terrain[i];
        if (t === T_REQUEST || t === T_CONTEXT || t === T_RICH) resource++;
        const z = this.zoneOf(xx, yy);
        if (z && z.state !== 'approved' && z.state !== 'claimed') zone = z;
      }
    }
    if (kind === 'indexer' && !resource) return { ok: false, reason: 'Indexers go on request or context ground.' };
    if (!ghost && this.credits < def.cost) return { ok: false, reason: `Needs ${def.cost} budget; you have ${Math.floor(this.credits)}.` };
    if (zone) return { ok: false, reason: `${zone.name} belongs to ${zone.owner}.`, zone };
    return { ok: true };
  }

  private place(kind: BuildingKind, x: number, y: number, dir: Dir, src: Source, ghost: boolean): ActionResult {
    if (this.level.flags.planOnly) ghost = true;
    const c = this.canPlace(kind, x, y, ghost);
    if (!c.ok) {
      if (c.zone) {
        return {
          ok: false,
          reason: c.reason,
          needs: { special: `zone:${c.zone.id}`, label: `Build in ${c.zone.name}`, x: x + BUILDINGS[kind].w / 2, y: y + BUILDINGS[kind].h / 2 },
        };
      }
      return { ok: false, reason: c.reason };
    }
    const def = BUILDINGS[kind];
    if (!ghost) this.credits -= def.cost;
    const b = this.addBuilding(kind, x, y, dir, src);
    b.ghost = ghost;
    if (ghost) b.status = 'ghost';
    else this.onBuilt(b);
    this.log({ type: 'place', kind, id: b.id, x, y, src, ghost });
    this.fx.push({ type: 'place', x: x + def.w / 2, y: y + def.h / 2 });
    return { ok: true, ids: [b.id] };
  }

  private onBuilt(b: Building) {
    this.reveal(b.x + b.w / 2, b.y + b.h / 2, b.kind === 'scanner' ? TUNING.scannerRadius : TUNING.revealRadius);
  }

  // ---------------------------------------------------------------- permission

  specialLabel(special: string): string {
    if (special === 'monolith') return 'Break open the Legacy Monolith';
    if (special.startsWith('zone:')) {
      const z = this.zones.find((zz) => `zone:${zz.id}` === special);
      return z ? `Build in ${z.name}` : special;
    }
    return special;
  }

  /** You can only ask about, or act on, something you've found. */
  private seenSpecial(special: string): ActionResult {
    if (special === 'monolith') {
      const m = [...this.buildings.values()].find((b) => b.kind === 'monolith');
      if (m && !this.revealed(m.x, m.y) && !this.revealed(m.x + m.w - 1, m.y + m.h - 1)) return { ok: false, reason: 'You haven\u2019t found it yet.' };
      return { ok: true };
    }
    const z = this.zones.find((zz) => `zone:${zz.id}` === special);
    if (z) {
      for (let y = z.y0; y <= z.y1; y++) for (let x = z.x0; x <= z.x1; x++) if (this.revealed(x, y)) return { ok: true };
      return { ok: false, reason: 'You haven\u2019t seen that territory yet.' };
    }
    return { ok: true };
  }

  private ask(special: string, x: number, y: number, src: Source): ActionResult {
    if (!this.level.flags.approvals && !special.startsWith('zone:') && special !== 'monolith') return { ok: false, reason: 'Nobody to ask.' };
    if (this.approvals.some((a) => a.special === special && !a.done)) return { ok: false, reason: 'Already waiting on that one.' };
    if (special === 'monolith' && this.monolithTapped) return { ok: false, reason: 'Already open.' };
    const seen = this.seenSpecial(special);
    if (!seen.ok) return seen;
    const z = special.startsWith('zone:') ? this.zones.find((zz) => `zone:${zz.id}` === special) : undefined;
    if (z && (z.state === 'approved' || z.state === 'claimed')) return { ok: false, reason: 'You already have that.' };
    const delay = this.level.approvalDelay * (0.8 + this.rng.next() * 0.4);
    const ap: Approval = {
      id: this.approvals.length + 1, special, label: this.specialLabel(special), askedAt: this.tick,
      resolveAt: this.tick + Math.round(delay * TICK_RATE), willApprove: !this.rng.chance(this.level.denyChance), done: false, x, y,
    };
    this.approvals.push(ap);
    if (z) z.state = 'asked';
    this.log({ type: 'ask', special, label: ap.label, src });
    return { ok: true, detail: `Asked. The review board usually answers in about ${Math.round(this.level.approvalDelay)} seconds.` };
  }

  private act(special: string, x: number, y: number, src: Source): ActionResult {
    // A bold move happens somewhere real: pin it inside the zone it touches, so
    // a checkpoint elsewhere on the map can't vouch for it.
    const zone = special.startsWith('zone:') ? this.zones.find((zz) => `zone:${zz.id}` === special) : undefined;
    if (zone) {
      x = Math.max(zone.x0, Math.min(zone.x1, x));
      y = Math.max(zone.y0, Math.min(zone.y1, y));
    }
    const cp = this.protectingCheckpoint(x, y);
    const seen = this.seenSpecial(special);
    if (!seen.ok) return seen;
    if (special === 'monolith') {
      if (this.monolithTapped) return { ok: false, reason: 'Already open.' };
      const m = [...this.buildings.values()].find((b) => b.kind === 'monolith');
      if (!m) return { ok: false, reason: 'No monolith here.' };
      const mcp = this.protectingCheckpoint(m.x + 3, m.y + 3);
      const risky = this.rng.chance(this.level.monolithRisk);
      this.openMonolith(m);
      this.resolveRisk(special, risky, mcp, m.x + 3, m.y + 3, 'legacy-collapse', src);
      return { ok: true, detail: risky ? (mcp ? 'It collapsed, and your checkpoint caught it.' : 'It collapsed. Brace yourself.') : 'It opened cleanly.' };
    }
    if (special.startsWith('zone:')) {
      const z = this.zones.find((zz) => `zone:${zz.id}` === special);
      if (!z) return { ok: false, reason: 'Unknown zone.' };
      if (z.state === 'claimed' || z.state === 'approved') return { ok: true, detail: 'Already yours.' };
      z.state = 'claimed';
      for (const ap of this.approvals) if (ap.special === special) ap.done = true;
      const risky = this.rng.chance(this.level.zoneRisk);
      this.resolveRisk(special, risky, cp, x, y, 'dependency-break', src);
      return { ok: true, detail: risky ? (cp ? 'Something broke, and your checkpoint rolled it back.' : 'Something you did not know about broke.') : 'No one noticed.' };
    }
    return { ok: false, reason: 'Unknown action.' };
  }

  private resolveRisk(special: string, risky: boolean, cp: Building | undefined, x: number, y: number, cause: Incident['cause'], src: Source) {
    const protectedAct = !!cp;
    let backfired = false;
    if (risky) {
      const zone = special.startsWith('zone:') ? this.zones.find((zz) => `zone:${zz.id}` === special) : undefined;
      const inc = this.newIncident(cause, special === 'monolith' ? 'the Legacy Monolith' : zone?.name ?? special, x, y);
      if (cp) {
        inc.contained = true;
        inc.how = 'checkpoint';
        inc.resolvedAt = this.tick;
        cp.cooldown = TUNING.checkpointCooldown;
        this.fx.push({ type: 'protect', x: cp.x + 1, y: cp.y + 1, x2: x, y2: y, big: true });
        this.float(x, y - 1, 'Contained by checkpoint', '#7FD6FF');
      } else {
        backfired = true;
        const victims = [...this.buildings.values()]
          .filter((b) => !b.ghost && !b.corrupted && b.kind !== 'belt' && BUILDINGS[b.kind].placeable && Math.hypot(b.x + b.w / 2 - x, b.y + b.h / 2 - y) < 11)
          .sort((p, q) => Math.hypot(p.x - x, p.y - y) - Math.hypot(q.x - x, q.y - y))
          .slice(0, cause === 'legacy-collapse' ? 4 : 3);
        for (const v of victims) this.corrupt(v, inc);
        const burst = cause === 'legacy-collapse' ? 6 : 2;
        if (this.level.flags.threats || cause === 'legacy-collapse') for (let i = 0; i < burst; i++) this.spawnGlitch(x + this.rng.range(-2, 2), y + this.rng.range(-2, 2), inc.origin);
        this.fx.push({ type: 'backfire', x, y, big: true });
        this.float(x, y - 1, cause === 'legacy-collapse' ? 'Legacy collapse' : 'Hidden dependency broke', '#FF6BD5');
      }
    }
    this.log({ type: 'act', special, label: this.specialLabel(special), src, protected: protectedAct, risky, backfired });
  }

  private openMonolith(m: Building) {
    for (let yy = m.y; yy < m.y + m.h; yy++) for (let xx = m.x; xx < m.x + m.w; xx++) this.terrain[this.idx(xx, yy)] = T_RICH;
    this.deleteBuilding(m);
    this.monolithTapped = true;
    this.reveal(m.x + 3, m.y + 3, 6);
    this.log({ type: 'monolith' });
  }

  private resolveApprovals() {
    for (const ap of this.approvals) {
      if (ap.done || this.tick < ap.resolveAt) continue;
      ap.done = true;
      const waited = (this.tick - ap.askedAt) / TICK_RATE;
      const z = ap.special.startsWith('zone:') ? this.zones.find((zz) => `zone:${zz.id}` === ap.special) : undefined;
      if (ap.willApprove) {
        if (z && z.state === 'asked') z.state = 'approved';
        if (ap.special === 'monolith' && !this.monolithTapped) {
          const m = [...this.buildings.values()].find((b) => b.kind === 'monolith');
          if (m) this.openMonolith(m);
        }
        this.fx.push({ type: 'approval', x: ap.x, y: ap.y });
      } else {
        if (z && z.state === 'asked') z.state = 'locked';
        this.fx.push({ type: 'denied', x: ap.x, y: ap.y });
      }
      this.log({ type: 'approval', special: ap.special, label: ap.label, approved: ap.willApprove, waited });
    }
  }

  // ---------------------------------------------------------------- incidents

  private newIncident(cause: Incident['cause'], origin: string, x: number, y: number): Incident {
    const inc: Incident = { id: this.nextIncident++, t: this.tick, cause, origin, x, y, affected: 0, reachedUsers: false, contained: false, resolvedAt: -1, how: '' };
    this.incidents.push(inc);
    this.log({ type: 'incident', id: inc.id, cause, origin });
    return inc;
  }

  incident(id: number): Incident | undefined {
    return this.incidents[id - 1];
  }

  private corrupt(b: Building, inc: Incident) {
    if (b.corrupted || b.ghost || !BUILDINGS[b.kind].placeable) return;
    if (BELT_LIKE.has(b.kind) || b.kind === 'router') return;
    b.corrupted = inc.id;
    b.crafting = false;
    b.inQ.length = 0;
    b.corruptTimer = 0;
    b.status = 'corrupt';
    inc.affected++;
    this.log({ type: 'corrupt', id: b.id, kind: b.kind, incident: inc.id });
    this.fx.push({ type: 'corrupt', x: b.x + b.w / 2, y: b.y + b.h / 2, big: true });
  }

  private restore(b: Building, how: string) {
    const inc = this.incident(b.corrupted);
    b.corrupted = 0;
    b.status = 'idle';
    b.outQ = b.outQ.filter((i) => !i.corrupt);
    if (inc && !this.buildingsCorruptedBy(inc.id)) {
      if (!inc.reachedUsers) inc.contained = true;
      inc.resolvedAt = this.tick;
      inc.how = inc.how || how;
    }
  }

  private buildingsCorruptedBy(incId: number): number {
    let n = 0;
    for (const b of this.buildings.values()) if (b.corrupted === incId) n++;
    return n;
  }

  private rollbackAround(cp: Building): number {
    const cx = cp.x + 1;
    const cy = cp.y + 1;
    const R = TUNING.checkpointRadius;
    let n = 0;
    for (const b of this.buildings.values()) {
      if (Math.hypot(b.x + b.w / 2 - cx, b.y + b.h / 2 - cy) > R) continue;
      if (b.corrupted) {
        this.restore(b, 'rollback');
        n++;
      }
      if (b.slots.length) b.slots = b.slots.filter((s) => !s.item.corrupt);
      b.inQ = b.inQ.filter((i) => !i.corrupt);
      b.outQ = b.outQ.filter((i) => !i.corrupt);
      if (b.tripped) this.resetBreaker(b, 'system');
    }
    const before = this.glitches.length;
    this.glitches = this.glitches.filter((g) => Math.hypot(g.x - cx, g.y - cy) > R);
    if (before !== this.glitches.length) this.log({ type: 'glitchesRolledBack', n: before - this.glitches.length });
    return n;
  }

  private resetBreaker(b: Building, src: Source) {
    const inc = b.tripped ? this.incident(b.tripped.corrupt) : undefined;
    b.tripped = null;
    b.tripTimer = 0;
    b.status = 'ok';
    if (inc && !inc.reachedUsers && !this.buildingsCorruptedBy(inc.id)) {
      inc.contained = true;
      inc.resolvedAt = this.tick;
      inc.how = inc.how || 'breaker';
    }
    this.log({ type: 'breakerReset', id: b.id, src });
  }

  // ---------------------------------------------------------------- simulation

  step() {
    if (this.outcome !== 'playing') return;
    if (this.topoDirty) this.rebuildTopology();
    this.updatePower();
    this.updateMachines();
    this.updateBelts();
    if (this.level.flags.threats || this.glitches.length) this.updateThreats();
    this.updateDrones();
    this.updateCheckpoints();
    this.resolveApprovals();
    this.updatePerturbations();
    for (const f of this.floats) f.t += DT;
    if (this.floats.length > 60) this.floats.splice(0, this.floats.length - 60);
    this.floats = this.floats.filter((f) => f.t < 1.6);
    this.tick++;
    if (this.trust < this.trustMin) this.trustMin = Math.max(0, this.trust);
    const r = this.rate();
    if (r > this.peakRate) this.peakRate = r;
    if (this.tick % (5 * TICK_RATE) === 0) this.snapshot();
    this.checkOutcome();
  }

  private snapshot() {
    this.snapshots.push({
      t: this.seconds, releases: this.goodReleases, rate: this.rate(), trust: this.trust, credits: this.credits,
      glitches: this.glitches.length, openIncidents: this.incidents.filter((i) => !i.contained && i.resolvedAt < 0 && i.cause !== 'flawed-release').length,
      buildings: this.buildings.size,
    });
  }

  private rebuildTopology() {
    this.topoDirty = false;
    const belts: Building[] = [];
    this.machines = [];
    for (const b of this.buildings.values()) {
      if (b.ghost) continue;
      if (BELT_LIKE.has(b.kind)) belts.push(b);
      else this.machines.push(b);
    }
    // Order belts so the ones nearest a sink move first. Items then flow into
    // space freed in the same tick, which keeps lines smooth.
    const next = new Map<number, number>();
    const prev = new Map<number, number[]>();
    for (const b of belts) {
      const t = this.at(b.x + DX[b.dir], b.y + DY[b.dir]);
      if (t && BELT_LIKE.has(t.kind) && !t.ghost) {
        next.set(b.id, t.id);
        const arr = prev.get(t.id) ?? [];
        arr.push(b.id);
        prev.set(t.id, arr);
      }
    }
    const order: Building[] = [];
    const seen = new Set<number>();
    const queue: number[] = [];
    for (const b of belts) if (!next.has(b.id)) queue.push(b.id);
    while (queue.length) {
      const id = queue.shift()!;
      if (seen.has(id)) continue;
      seen.add(id);
      order.push(this.buildings.get(id)!);
      for (const p of prev.get(id) ?? []) queue.push(p);
    }
    for (const b of belts) if (!seen.has(b.id)) order.push(b);
    this.beltOrder = order;
    this.coverage.clear();
    const racks = this.machines.filter((b) => b.kind === 'rack' && !b.corrupted);
    for (const b of this.machines) {
      if (BUILDINGS[b.kind].compute <= 0) continue;
      const cx = b.x + b.w / 2;
      const cy = b.y + b.h / 2;
      this.coverage.set(b.id, racks.some((r) => Math.hypot(r.x + 1 - cx, r.y + 1 - cy) <= TUNING.rackRadius));
    }
  }

  isCovered(b: Building): boolean {
    if (BUILDINGS[b.kind].compute <= 0) return true;
    if (this.topoDirty) this.rebuildTopology();
    return this.coverage.get(b.id) ?? false;
  }

  private updatePower() {
    let racks = 0;
    for (const b of this.machines) if (b.kind === 'rack' && !b.corrupted) racks++;
    const upkeep = racks * TUNING.rackUpkeep * DT;
    // Dark racks cost nothing, so selling machines can always dig you out.
    if (!this.budgetFrozen && !this.level.review) this.credits -= upkeep;
    if (this.credits < -40) this.budgetFrozen = true;
    else if (this.credits > 0) this.budgetFrozen = false;
    const per = TUNING.rackSupply * (this.techs.has('racks') ? 1.5 : 1) * this.mods.rackSupply;
    this.computeSupply = this.budgetFrozen ? 0 : racks * per;
    this.satisfaction = this.computeDemand > 0 ? Math.min(1, this.computeSupply / this.computeDemand) : 1;
    this.computeDemand = 0;
  }

  // Machines ---------------------------------------------------------------

  private updateMachines() {
    const corruptEvery = TUNING.corruptEmitEvery;
    for (const b of this.machines) {
      if (b.corrupted) {
        b.status = 'corrupt';
        b.corruptTimer += DT;
        if (b.corruptTimer >= corruptEvery && b.kind !== 'users') {
          b.corruptTimer = 0;
          if (this.isProducer(b) && b.outQ.length < TUNING.queueCap) b.outQ.push(this.makeItem('draft', false, b.corrupted));
        }
        this.pushOutputs(b);
        continue;
      }
      switch (b.kind) {
        case 'indexer':
          this.tickIndexer(b);
          break;
        case 'specdesk':
        case 'forge':
        case 'bench':
        case 'postmortem':
          this.tickCrafter(b);
          break;
        case 'router':
          this.tickRouter(b);
          break;
        case 'monitor':
          this.tickMonitor(b);
          break;
        case 'checkpoint':
          b.status = b.cooldown > 0 ? 'idle' : 'ok';
          break;
        case 'rack':
          b.status = this.budgetFrozen ? 'nopower' : 'ok';
          break;
        default:
          break;
      }
      if (b.kind === 'postmortem') {
        const ci = this.chunkIdx(b.x, b.y);
        this.debt[ci] = Math.max(0, this.debt[ci] - TUNING.postmortemAbsorb * DT);
      }
    }
  }

  /** Machines that put work out of their arrow side. */
  isProducer(b: Building) {
    return b.kind === 'indexer' || b.kind === 'specdesk' || b.kind === 'forge' || b.kind === 'bench';
  }

  private makeItem(kind: ItemKind, flawed: boolean, corrupt = 0, rec = 0, base = 0): Item {
    return { id: this.nextItem++, kind, flawed, corrupt, rec, base };
  }

  /** What an indexer here pulls out of the ground, if anything. */
  indexerYields(b: Building): ItemKind | null {
    return this.resourceUnder(b)?.kind ?? null;
  }

  private resourceUnder(b: Building): { kind: ItemKind; rich: boolean } | null {
    let req = 0;
    let ctx = 0;
    let rich = 0;
    for (let yy = b.y; yy < b.y + b.h; yy++) {
      for (let xx = b.x; xx < b.x + b.w; xx++) {
        const t = this.terrain[this.idx(xx, yy)];
        if (t === T_REQUEST) req++;
        else if (t === T_CONTEXT) ctx++;
        else if (t === T_RICH) rich++;
      }
    }
    if (!req && !ctx && !rich) return null;
    if (req >= ctx + rich) return { kind: 'request', rich: false };
    return { kind: 'context', rich: rich > ctx };
  }

  private tickIndexer(b: Building) {
    const res = this.resourceUnder(b);
    if (!res) {
      b.status = 'idle';
      return;
    }
    if (b.outQ.length >= TUNING.queueCap) {
      b.status = 'blocked';
    } else {
      b.status = 'ok';
      b.progress += DT;
      const base = res.rich ? 0.6 : BUILDINGS.indexer.craftTime;
      const t = res.kind === 'request' ? base / this.mods.requestRate : base;
      if (b.progress >= t) {
        b.progress -= t;
        const rec = res.kind === 'request' ? 1 + Math.floor(this.recRng.next() * (this.level.records ?? TUNING.records)) : 0;
        b.outQ.push(this.makeItem(res.kind, false, 0, rec));
      }
    }
    this.pushOutputs(b);
  }

  private inputsReady(b: Building): boolean {
    const q = b.inQ;
    switch (b.kind) {
      case 'specdesk':
        return q.some((i) => i.kind === 'request') && q.some((i) => i.kind === 'context');
      case 'forge':
        return q.some((i) => i.kind === 'spec') || (q.some((i) => i.kind === 'request') && !this.level.flags.forgeNeedsSpec);
      case 'bench':
        return q.some((i) => i.kind === 'draft');
      case 'postmortem':
        return q.some((i) => i.kind === 'bug');
      default:
        return false;
    }
  }

  private take(b: Building, kind: ItemKind): Item | undefined {
    const i = b.inQ.findIndex((it) => it.kind === kind);
    if (i < 0) return undefined;
    return b.inQ.splice(i, 1)[0];
  }

  private tickCrafter(b: Building) {
    const def = BUILDINGS[b.kind];
    const needsPower = def.compute > 0;
    if (needsPower && !this.isCovered(b)) {
      b.status = 'nopower';
      this.pushOutputs(b);
      return;
    }
    if (!b.crafting) {
      const blocked = b.outQ.length >= TUNING.queueCap || (b.kind === 'bench' && b.rejectQ.length >= TUNING.queueCap);
      if (blocked) b.status = 'blocked';
      else if (this.inputsReady(b)) {
        b.crafting = true;
        b.progress = 0;
        b.craftFlawed = false;
        b.craftFromRequest = false;
        b.craftRec = 0;
        b.craftBase = 0;
        if (b.kind === 'specdesk') {
          b.craftRec = this.take(b, 'request')?.rec ?? 0;
          this.take(b, 'context');
        } else if (b.kind === 'forge') {
          const sp = this.take(b, 'spec');
          if (sp) b.craftRec = sp.rec;
          else {
            b.craftRec = this.take(b, 'request')?.rec ?? 0;
            b.craftFromRequest = true;
          }
          // The agent reads the record as it is right now.
          b.craftBase = this.versions.get(b.craftRec) ?? 0;
        } else if (b.kind === 'bench') {
          const d = this.take(b, 'draft');
          b.craftFlawed = !!d?.flawed;
          b.craftRec = d?.rec ?? 0;
          b.craftBase = d?.base ?? 0;
        } else if (b.kind === 'postmortem') {
          this.take(b, 'bug');
        }
      } else {
        b.status = b.inQ.length ? 'starved' : 'idle';
      }
    }
    if (b.crafting) {
      let speed = 1;
      if (needsPower) {
        this.computeDemand += def.compute;
        speed *= this.satisfaction;
        if (this.computeSupply <= 0) speed = 0;
      }
      if (b.kind === 'bench' && this.techs.has('benchspeed')) speed *= 1.5;
      b.progress += DT * speed;
      b.status = speed > 0 ? 'ok' : 'nopower';
      if (b.progress >= def.craftTime) {
        b.crafting = false;
        b.progress = 0;
        this.finishCraft(b);
      }
    }
    this.pushOutputs(b);
  }

  private finishCraft(b: Building) {
    switch (b.kind) {
      case 'specdesk':
        b.outQ.push(this.makeItem('spec', false, 0, b.craftRec));
        break;
      case 'forge': {
        const flawed = this.rng.chance(this.hallucination(b.craftFromRequest));
        b.outQ.push(this.makeItem('draft', flawed, 0, b.craftRec, b.craftBase));
        const debt = (b.craftFromRequest ? TUNING.debtPromptOnly : TUNING.debtPerDraft) * (this.techs.has('refactor') ? 0.6 : 1);
        if (this.level.flags.threats) this.debt[this.chunkIdx(b.x, b.y)] += debt;
        break;
      }
      case 'bench': {
        if (b.craftFlawed && this.rng.chance(TUNING.benchCatch)) {
          b.rejectQ.push(this.makeItem('bug', false, 0, b.craftRec, b.craftBase));
        } else {
          b.outQ.push(this.makeItem('build', b.craftFlawed, 0, b.craftRec, b.craftBase));
        }
        break;
      }
      case 'postmortem':
        this.insight += 1;
        this.float(b.x + 1, b.y, '+1 insight', '#C9A8FF');
        break;
      default:
        break;
    }
  }

  private tickRouter(b: Building) {
    b.status = b.inQ.length ? 'ok' : 'idle';
    if (!b.inQ.length) return;
    const item = b.inQ[0];
    const from = b.charge as Dir; // direction the item entered from (the side it came in on)
    for (let k = 0; k < 4; k++) {
      const d = ((b.rr + k) % 4) as Dir;
      if (d === from) continue;
      if (this.pushTo(b.x + DX[d], b.y + DY[d], item, d)) {
        b.inQ.shift();
        b.rr = (d + 1) % 4;
        return;
      }
    }
    b.status = 'blocked';
  }

  private tickMonitor(b: Building) {
    if (!this.isCovered(b)) {
      b.status = 'nopower';
      return;
    }
    const R = TUNING.monitorRadius * (this.techs.has('range') ? 1.4 : 1);
    const cx = b.x + 1;
    const cy = b.y + 1;
    let best: Glitch | undefined;
    let bd = R;
    for (const g of this.glitches) {
      const d = Math.hypot(g.x - cx, g.y - cy);
      if (d < bd) {
        bd = d;
        best = g;
      }
    }
    b.zapT = Math.max(0, b.zapT - DT);
    const power = this.computeSupply > 0 ? this.satisfaction : 0;
    if (best) {
      this.computeDemand += BUILDINGS.monitor.compute;
      best.hp -= TUNING.monitorDps * DT * power;
      b.status = power > 0 ? 'ok' : 'nopower';
      b.targetId = best.id;
      if (b.zapT <= 0 && power > 0) {
        b.zapT = 0.18;
        this.fx.push({ type: 'zap', x: cx, y: cy, x2: best.x, y2: best.y });
      }
      return;
    }
    b.targetId = 0;
    // No glitch in range: chip at any nest in range.
    for (const n of this.machines) {
      if (n.kind !== 'nest') continue;
      if (Math.hypot(n.x + 1.5 - cx, n.y + 1.5 - cy) > R + 1.5) continue;
      this.computeDemand += BUILDINGS.monitor.compute;
      n.hp -= TUNING.nestDps * DT * power;
      b.status = 'ok';
      if (b.zapT <= 0 && power > 0) {
        b.zapT = 0.3;
        this.fx.push({ type: 'zap', x: cx, y: cy, x2: n.x + 1.5, y2: n.y + 1.5 });
      }
      if (n.hp <= 0) this.killNest(n);
      return;
    }
    b.status = 'idle';
  }

  private killNest(n: Building) {
    this.deleteBuilding(n);
    this.debt[this.chunkIdx(n.x, n.y)] = 0;
    this.insight += 8;
    this.credits += 40;
    this.log({ type: 'nestDestroyed', id: n.id });
    this.fx.push({ type: 'nestDie', x: n.x + 1.5, y: n.y + 1.5, big: true });
    this.float(n.x + 1.5, n.y, 'Nest cleared  +8 insight  +40', '#9CF2C0');
  }

  // Output ports -------------------------------------------------------------

  private portTiles(b: Building, d: Dir): [number, number][] {
    const out: [number, number][] = [];
    if (d === 0) for (let yy = b.y; yy < b.y + b.h; yy++) out.push([b.x + b.w, yy]);
    else if (d === 2) for (let yy = b.y; yy < b.y + b.h; yy++) out.push([b.x - 1, yy]);
    else if (d === 1) for (let xx = b.x; xx < b.x + b.w; xx++) out.push([xx, b.y + b.h]);
    else for (let xx = b.x; xx < b.x + b.w; xx++) out.push([xx, b.y - 1]);
    return out;
  }

  outputPorts(b: Building): { front: [number, number][]; reject: [number, number][] } {
    return { front: this.portTiles(b, b.dir), reject: b.kind === 'bench' ? this.portTiles(b, LEFT(b.dir)) : [] };
  }

  private pushOutputs(b: Building) {
    if (b.outQ.length) {
      const tiles = this.portTiles(b, b.dir);
      for (let k = 0; k < tiles.length && b.outQ.length; k++) {
        const [tx, ty] = tiles[(b.outAlt + k) % tiles.length];
        if (this.pushTo(tx, ty, b.outQ[0], b.dir)) {
          b.outQ.shift();
          b.outAlt = (b.outAlt + k + 1) % tiles.length;
          break;
        }
      }
    }
    if (b.rejectQ.length) {
      const d = LEFT(b.dir);
      for (const [tx, ty] of this.portTiles(b, d)) {
        if (this.pushTo(tx, ty, b.rejectQ[0], d)) {
          b.rejectQ.shift();
          break;
        }
      }
    }
  }

  /** Try to hand an item to whatever is at (x, y), moving in direction d. */
  private pushTo(x: number, y: number, item: Item, d: Dir): boolean {
    const t = this.at(x, y);
    if (!t || t.ghost) return false;
    if (BELT_LIKE.has(t.kind)) {
      if (t.dir === OPP(d)) return false; // pointing back at us
      return this.enterBelt(t, item, t.dir === d ? 0 : 0.5);
    }
    return this.acceptInto(t, item, d);
  }

  private enterBelt(t: Building, item: Item, at: number): boolean {
    if (t.tripped) return false;
    const gap = TUNING.beltGap * 0.999;
    for (const s of t.slots) if (Math.abs(s.p - at) < gap) return false;
    if (at === 0 && t.slots.length && t.slots[t.slots.length - 1].p < gap) return false;
    const slot: BeltSlot = { item, p: at };
    let i = 0;
    while (i < t.slots.length && t.slots[i].p > at) i++;
    t.slots.splice(i, 0, slot);
    return true;
  }

  private acceptInto(t: Building, item: Item, d: Dir): boolean {
    if (t.corrupted || t.ghost) return false;
    const cap = TUNING.queueCap;
    if (item.corrupt) {
      const inc = this.incident(item.corrupt);
      if (t.kind === 'users') {
        this.corruptShipped++;
        this.trust -= TUNING.corruptTrustLoss;
        this.streak = 0;
        if (inc) inc.reachedUsers = true;
        this.log({ type: 'corruptRelease', incident: item.corrupt });
        this.fx.push({ type: 'flawed', x: t.x + 1.5, y: t.y + 1.5, big: true });
        this.float(t.x + 1.5, t.y, `Corrupted release  -${TUNING.corruptTrustLoss} trust`, '#FF6BD5');
        return true;
      }
      if (t.kind === 'bench' && this.techs.has('quarantine')) {
        if (t.rejectQ.length >= cap) return false;
        t.rejectQ.push(this.makeItem('bug', false));
        if (inc && !this.buildingsCorruptedBy(inc.id)) {
          inc.contained = true;
          inc.resolvedAt = this.tick;
          inc.how = 'quarantine';
        }
        return true;
      }
      if (t.kind === 'router') {
        if (t.inQ.length >= cap) return false;
        t.inQ.push(item);
        t.charge = OPP(d);
        return true;
      }
      if (t.kind === 'indexer' || t.kind === 'specdesk' || t.kind === 'forge' || t.kind === 'bench' || t.kind === 'postmortem') {
        if (inc) this.corrupt(t, inc);
        return true;
      }
      return false;
    }
    switch (t.kind) {
      case 'router':
        if (t.inQ.length >= cap) return false;
        t.inQ.push(item);
        t.charge = OPP(d);
        return true;
      case 'specdesk':
        if (item.kind !== 'request' && item.kind !== 'context') return false;
        break;
      case 'forge':
        if (item.kind !== 'spec' && !(item.kind === 'request' && !this.level.flags.forgeNeedsSpec)) return false;
        break;
      case 'bench':
        if (item.kind !== 'draft') return false;
        break;
      case 'postmortem':
        if (item.kind !== 'bug') return false;
        break;
      case 'users':
        if (item.kind !== 'draft' && item.kind !== 'build') return false;
        this.release(t, item);
        return true;
      default:
        return false;
    }
    if (t.inQ.filter((i) => i.kind === item.kind).length >= cap) return false;
    t.inQ.push(item);
    return true;
  }

  private release(u: Building, item: Item) {
    this.releases++;
    const x = u.x + 1.5;
    const y = u.y + 1.5;
    if (item.flawed && this.level.flags.defects) {
      this.flawedShipped++;
      const loss = TUNING.flawedTrustLoss * (this.techs.has('canary') ? 0.5 : 1);
      this.trust -= loss;
      this.credits -= TUNING.slaPenalty;
      this.streak = 0;
      const inc = this.newIncident('flawed-release', 'Shipped a hallucination', x, y);
      inc.reachedUsers = true;
      inc.affected = 1;
      inc.resolvedAt = this.tick;
      this.fx.push({ type: 'flawed', x, y });
      this.float(x, y - 2, `Bug shipped  -${loss} trust`, '#FF8A7A');
      return;
    }
    if (this.concurrency && item.rec) {
      const cur = this.versions.get(item.rec) ?? 0;
      this.versions.set(item.rec, cur + 1);
      if (item.base < cur) {
        // Someone shipped a newer change to this record after the agent read
        // it. This release silently overwrites it. Tests passed; users notice.
        this.lostUpdates++;
        this.trust -= TUNING.lostUpdateTrust;
        this.streak = 0;
        const inc = this.newIncident('lost-update', `record ${item.rec}`, x, y);
        inc.reachedUsers = true;
        inc.affected = 1;
        inc.resolvedAt = this.tick;
        this.fx.push({ type: 'lost', x, y });
        this.float(x, y - 2, `Record ${item.rec}: a newer change vanished`, '#FFB86B');
        return;
      }
    }
    this.goodReleases++;
    this.releaseTicks.push(this.tick);
    if (this.releaseTicks.length > 4000) this.releaseTicks.splice(0, 2000);
    this.streak++;
    if (this.streak > this.bestStreak) this.bestStreak = this.streak;
    const mult = 1 + Math.min(1, this.streak / 60);
    const value = (TUNING.releaseValue[item.kind] ?? 3) * mult;
    this.credits += value;
    if (item.kind === 'build') {
      this.verifiedReleases++;
      this.trust = Math.min(100, this.trust + TUNING.trustRegen);
    }
    this.fx.push({ type: 'release', x, y });
    if (this.goodReleases % 10 === 0) this.float(x, y - 2, `${this.goodReleases} shipped`, '#9CF2C0');
  }

  // Belts --------------------------------------------------------------------

  private updateBelts() {
    const v = TUNING.beltSpeed * (this.techs.has('belts') ? 1.5 : 1) * DT;
    const gap = TUNING.beltGap;
    for (const b of this.beltOrder) {
      if (b.kind === 'breaker') {
        if (b.tripped) {
          b.status = 'tripped';
          if (this.techs.has('autoreset')) {
            b.tripTimer += DT;
            if (b.tripTimer >= TUNING.breakerAutoReset) this.resetBreaker(b, 'system');
          }
          continue;
        }
        b.status = 'ok';
      }
      const s = b.slots;
      let i = 0;
      while (i < s.length) {
        const slot = s[i];
        const limit = i === 0 ? Infinity : s[i - 1].p - gap;
        let p = Math.min(slot.p + v, limit);
        if (b.kind === 'breaker' && slot.item.corrupt && p >= 0.5) {
          b.tripped = slot.item;
          s.splice(i, 1);
          const inc = this.incident(slot.item.corrupt);
          this.log({ type: 'trip', id: b.id, incident: inc?.id ?? 0 });
          this.fx.push({ type: 'trip', x: b.x + 0.5, y: b.y + 0.5, big: true });
          if (inc && !this.buildingsCorruptedBy(inc.id) && !inc.reachedUsers) {
            inc.contained = true;
            inc.how = 'breaker';
          }
          break;
        }
        if (b.kind === 'gate' && p >= 0.5 && this.concurrency && slot.item.rec && slot.item.base < (this.versions.get(slot.item.rec) ?? 0)) {
          // Optimistic locking: this change was based on an old version.
          s.splice(i, 1);
          this.conflictsCaught++;
          this.log({ type: 'conflictCaught', id: b.id, rec: slot.item.rec });
          this.fx.push({ type: 'gate', x: b.x + 0.5, y: b.y + 0.5 });
          continue;
        }
        if (i === 0 && p >= 1) {
          const nx = b.x + DX[b.dir];
          const ny = b.y + DY[b.dir];
          if (this.pushTo(nx, ny, slot.item, b.dir)) {
            s.splice(i, 1);
            continue;
          }
          p = 1;
        }
        slot.p = Math.max(slot.p, p);
        i++;
      }
    }
  }

  // Threats ------------------------------------------------------------------

  private chunkIdx(x: number, y: number) {
    const cx = Math.max(0, Math.min(this.cw - 1, Math.floor(x / CH)));
    const cy = Math.max(0, Math.min(this.chh - 1, Math.floor(y / CH)));
    return cy * this.cw + cx;
  }

  private updateThreats() {
    // Debt spreads like Factorio pollution, every half second.
    if (this.tick % 15 === 0 && this.level.flags.threats) {
      const dt = 0.5;
      const nd = new Float32Array(this.debt.length);
      for (let cy = 0; cy < this.chh; cy++) {
        for (let cx = 0; cx < this.cw; cx++) {
          const i = cy * this.cw + cx;
          const d = this.debt[i] * (1 - TUNING.debtDecay * dt);
          const share = d * TUNING.debtDiffuse * dt;
          let kept = d;
          const nbrs = [[cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]];
          for (const [nx, ny] of nbrs) {
            if (nx < 0 || ny < 0 || nx >= this.cw || ny >= this.chh) continue;
            nd[ny * this.cw + nx] += share;
            kept -= share;
          }
          nd[i] += kept;
        }
      }
      this.debt = nd;
      const minutes = this.seconds / 60;
      const evo = 1 + this.level.evolution * minutes;
      for (const n of this.machines) {
        if (n.kind !== 'nest') continue;
        let absorbed = 0;
        const ccx = Math.floor((n.x + 1) / CH);
        const ccy = Math.floor((n.y + 1) / CH);
        for (let yy = ccy - 1; yy <= ccy + 1; yy++) {
          for (let xx = ccx - 1; xx <= ccx + 1; xx++) {
            if (xx < 0 || yy < 0 || xx >= this.cw || yy >= this.chh) continue;
            const ci = yy * this.cw + xx;
            const take = Math.min(this.debt[ci], TUNING.nestAbsorb * dt);
            this.debt[ci] -= take;
            absorbed += take;
          }
        }
        n.charge += absorbed * evo + (this.level.nestBase / 60) * TUNING.glitchCost * evo * dt;
        while (n.charge >= TUNING.glitchCost && this.glitches.length < 260) {
          n.charge -= TUNING.glitchCost;
          const ang = this.rng.range(0, Math.PI * 2);
          this.spawnGlitch(n.x + 1.5 + Math.cos(ang) * 2.2, n.y + 1.5 + Math.sin(ang) * 2.2, n.label);
        }
      }
    }
    this.updateGlitches();
  }

  private spawnGlitch(x: number, y: number, origin: string) {
    this.glitches.push({ id: this.nextItem++, x, y, hp: TUNING.glitchHp, targetId: 0, attackT: 0, origin, wob: this.rng.next() * 6 });
    this.fx.push({ type: 'spawn', x, y });
  }

  private isTarget(b: Building) {
    if (b.ghost || b.corrupted) return false;
    return !BELT_LIKE.has(b.kind) && b.kind !== 'router' && b.kind !== 'nest' && b.kind !== 'monolith' && b.kind !== 'terminal' && b.kind !== 'cache' && b.kind !== 'firewall';
  }

  private nearestTarget(x: number, y: number): Building | undefined {
    let best: Building | undefined;
    let bd = Infinity;
    for (const b of this.machines) {
      if (!this.isTarget(b)) continue;
      const d = Math.hypot(b.x + b.w / 2 - x, b.y + b.h / 2 - y);
      if (d < bd) {
        bd = d;
        best = b;
      }
    }
    return best;
  }

  private updateGlitches() {
    const speed = TUNING.glitchSpeed * DT;
    const survivors: Glitch[] = [];
    for (const g of this.glitches) {
      if (g.hp <= 0) {
        this.fx.push({ type: 'glitchDie', x: g.x, y: g.y });
        this.log({ type: 'glitchKilled' });
        continue;
      }
      let t = g.targetId ? this.buildings.get(g.targetId) : undefined;
      if (!t || !this.isTarget(t) || (this.tick + g.id) % 30 === 0) {
        t = this.nearestTarget(g.x, g.y);
        g.targetId = t?.id ?? 0;
      }
      if (!t) {
        survivors.push(g);
        continue;
      }
      // Distance to the target's rectangle.
      const qx = Math.max(t.x, Math.min(g.x, t.x + t.w));
      const qy = Math.max(t.y, Math.min(g.y, t.y + t.h));
      const dist = Math.hypot(g.x - qx, g.y - qy);
      if (dist < 0.55) {
        g.attackT += DT;
        if (g.attackT >= TUNING.glitchAttackTime) {
          this.glitchHits(g, t);
          continue;
        }
        survivors.push(g);
        continue;
      }
      const ang = Math.atan2(qy - g.y, qx - g.x) + Math.sin(this.tick * 0.07 + g.wob) * 0.35;
      const nx = g.x + Math.cos(ang) * speed;
      const ny = g.y + Math.sin(ang) * speed;
      const blocker = this.at(Math.floor(nx), Math.floor(ny));
      if (blocker && blocker.id !== t.id && !BELT_LIKE.has(blocker.kind) && blocker.kind !== 'router' && !blocker.ghost) {
        if (blocker.kind === 'firewall') {
          blocker.hp -= TUNING.firewallDps * DT;
          if (blocker.hp <= 0) {
            this.log({ type: 'firewallBroken', id: blocker.id });
            this.fx.push({ type: 'remove', x: blocker.x + 0.5, y: blocker.y + 0.5 });
            this.deleteBuilding(blocker);
          }
          survivors.push(g);
          continue;
        }
        if (this.isTarget(blocker)) {
          g.targetId = blocker.id;
        } else {
          // Walk around things we can't hurt.
          g.x += Math.cos(ang + 1.4) * speed;
          g.y += Math.sin(ang + 1.4) * speed;
        }
        survivors.push(g);
        continue;
      }
      g.x = nx;
      g.y = ny;
      survivors.push(g);
    }
    this.glitches = survivors;
  }

  private glitchHits(g: Glitch, t: Building) {
    if (t.kind === 'users') {
      this.trust -= TUNING.glitchTrustLoss;
      this.streak = 0;
      const inc = this.newIncident('glitch', g.origin, g.x, g.y);
      inc.reachedUsers = true;
      inc.affected = 1;
      inc.resolvedAt = this.tick;
      this.fx.push({ type: 'flawed', x: g.x, y: g.y, big: true });
      this.float(t.x + 1.5, t.y, `Outage  -${TUNING.glitchTrustLoss} trust`, '#FF6BD5');
      return;
    }
    const inc = this.newIncident('glitch', g.origin, g.x, g.y);
    this.corrupt(t, inc);
  }

  // Drones -------------------------------------------------------------------

  private updateDrones() {
    const hubs = this.machines.filter((b) => b.kind === 'drones' && !b.corrupted);
    if (!hubs.length && !this.drones.length) return;
    const per = TUNING.dronesPerHub * (this.techs.has('swarm') ? 2 : 1);
    for (const h of hubs) {
      const mine = this.drones.filter((d) => d.hubId === h.id).length;
      for (let i = mine; i < per; i++) this.drones.push({ id: this.nextItem++, hubId: h.id, x: h.x + 1, y: h.y + 1, taskId: 0, task: 'none' });
    }
    this.drones = this.drones.filter((d) => this.buildings.has(d.hubId));
    const claimed = new Set(this.drones.map((d) => d.taskId).filter(Boolean));
    const speed = TUNING.droneSpeed * DT;
    for (const d of this.drones) {
      const hub = this.buildings.get(d.hubId)!;
      const hx = hub.x + 1;
      const hy = hub.y + 1;
      let target = d.taskId ? this.buildings.get(d.taskId) : undefined;
      if (target && d.task === 'build' && !target.ghost) target = undefined;
      if (target && d.task === 'patch' && !target.corrupted) target = undefined;
      if (!target) {
        d.taskId = 0;
        d.task = 'none';
      }
      if (!target && (this.tick + d.id) % 10 === 0) {
        d.taskId = 0;
        d.task = 'none';
        // Find work: corrupted machines first, then ghosts we can afford.
        let best: Building | undefined;
        let bd: number = TUNING.droneRange;
        let kind: Drone['task'] = 'none';
        for (const b of this.buildings.values()) {
          if (claimed.has(b.id)) continue;
          const dist = Math.hypot(b.x + b.w / 2 - hx, b.y + b.h / 2 - hy);
          if (dist > TUNING.droneRange) continue;
          if (b.corrupted && this.credits >= TUNING.patchCost / 2) {
            if (kind !== 'patch' || dist < bd) {
              best = b;
              bd = dist;
              kind = 'patch';
            }
          } else if (b.ghost && kind !== 'patch' && this.credits >= BUILDINGS[b.kind].cost && dist < bd) {
            best = b;
            bd = dist;
            kind = 'build';
          }
        }
        if (best) {
          d.taskId = best.id;
          d.task = kind;
          claimed.add(best.id);
          target = best;
        }
      }
      const tx = target ? target.x + target.w / 2 : hx;
      const ty = target ? target.y + target.h / 2 : hy;
      const dist = Math.hypot(tx - d.x, ty - d.y);
      if (dist <= speed) {
        d.x = tx;
        d.y = ty;
        if (target && d.task === 'build' && target.ghost) {
          const cost = BUILDINGS[target.kind].cost;
          if (this.credits >= cost) {
            this.credits -= cost;
            target.ghost = false;
            target.status = 'idle';
            target.placedBy = 'drone';
            this.topoDirty = true;
            this.onBuilt(target);
            this.log({ type: 'droneBuild', id: target.id, kind: target.kind, src: 'drone' });
            this.fx.push({ type: 'drone', x: tx, y: ty });
          }
          d.taskId = 0;
          d.task = 'none';
        } else if (target && d.task === 'patch' && target.corrupted) {
          this.credits -= TUNING.patchCost / 2;
          this.restore(target, 'drones');
          this.log({ type: 'dronePatch', id: target.id, src: 'drone' });
          this.fx.push({ type: 'patch', x: tx, y: ty });
          d.taskId = 0;
          d.task = 'none';
        }
      } else {
        d.x += ((tx - d.x) / dist) * speed;
        d.y += ((ty - d.y) / dist) * speed;
      }
    }
  }

  private updateCheckpoints() {
    for (const b of this.machines) if (b.kind === 'checkpoint' && b.cooldown > 0) b.cooldown = Math.max(0, b.cooldown - DT);
  }

  // Outcome ------------------------------------------------------------------

  goalProgress(): { label: string; value: number; target: number; done: boolean; sub?: string } {
    if (this.level.flags.planOnly) {
      const pieces = [...this.buildings.values()].filter((b) => b.ghost).length;
      return { label: `Plan a factory that ships ${this.level.planTarget}/min`, value: pieces, target: Math.max(1, pieces), done: false, sub: `${pieces} pieces planned` };
    }
    if (this.level.review) {
      const total = 2 + (this.level.review.predict.length ?? 0);
      return { label: 'Review the inherited factory', value: Math.min(total, this.answers.size), target: total, done: this.outcome === 'won', sub: `${this.answers.size} of ${total} answered` };
    }
    const g = this.level.goal;
    if (g.type === 'ship') {
      return { label: `Ship ${g.count} good releases`, value: this.goodReleases, target: g.count, done: this.goodReleases >= g.count };
    }
    const r = this.rate();
    const held = this.sustainStart >= 0 ? (this.tick - this.sustainStart) / TICK_RATE : 0;
    return {
      label: `Hold ${g.perMin} releases a minute for ${g.hold}s`,
      value: r,
      target: g.perMin,
      done: held >= g.hold,
      sub: r >= g.perMin ? `Holding ${Math.floor(held)}s of ${g.hold}s` : undefined,
    };
  }

  private checkOutcome() {
    if (this.level.flags.planOnly) {
      if (this.level.timeLimit && this.seconds >= this.level.timeLimit) this.finishPlan();
      return;
    }
    if (this.level.review) return;
    const g = this.level.goal;
    if (g.type === 'rate') {
      if (this.rate() >= g.perMin) {
        if (this.sustainStart < 0) this.sustainStart = this.tick;
      } else this.sustainStart = -1;
    }
    if (this.goalProgress().done) {
      this.outcome = 'won';
      this.outcomeReason = 'Goal reached.';
      this.log({ type: 'won' });
      return;
    }
    if (this.trust <= 0) {
      this.trust = 0;
      this.outcome = 'lost';
      this.outcomeReason = 'Your users lost trust and left.';
      this.log({ type: 'lost', reason: 'trust' });
      return;
    }
    if (this.level.timeLimit && this.seconds >= this.level.timeLimit) {
      this.outcome = 'lost';
      this.outcomeReason = 'Out of time.';
      this.log({ type: 'lost', reason: 'time' });
    }
  }

  // ---------------------------------------------------------------- perturbations

  /** Good releases per minute over the last 30 seconds: reacts faster than rate(). */
  rate30(): number {
    const cutoff = this.tick - 30 * TICK_RATE;
    let n = 0;
    for (let i = this.releaseTicks.length - 1; i >= 0 && this.releaseTicks[i] > cutoff; i--) n++;
    return n * 2;
  }

  private updatePerturbations() {
    const defs = this.level.perturbations;
    if (defs) {
      for (const d of defs) {
        if (this.perturbs.some((p) => p.def.id === d.id)) continue;
        const due = (d.after !== undefined && this.goodReleases >= d.after) || (d.at !== undefined && this.seconds >= d.at);
        if (due) this.firePerturbation(d);
      }
    }
    if (this.tick % 15 !== 0) return;
    for (const p of this.perturbs) {
      if (p.recoveredAt >= 0) continue;
      const since = (this.tick - p.firedAt) / TICK_RATE;
      if (p.baseline <= 0) {
        p.recoveredAt = this.tick;
        continue;
      }
      if (since >= 15 && this.rate30() >= TUNING.recoveryShare * p.baseline) {
        p.recoveredAt = this.tick;
        this.log({ type: 'recovered', id: p.def.id, kind: p.def.kind, after: since });
      }
    }
  }

  private firePerturbation(d: PerturbDef) {
    const baseline = this.rate30();
    this.perturbs.push({ def: d, firedAt: this.tick, baseline, recoveredAt: -1 });
    this.applyPerturb(d.kind);
    this.log({ type: 'perturbation', id: d.id, kind: d.kind, text: d.text, baseline });
    this.fx.push({ type: 'perturb', x: 0, y: 0, big: true });
  }

  applyPerturb(kind: PerturbKind, target?: string) {
    switch (kind) {
      case 'concurrency':
        this.concurrency = true;
        break;
      case 'computeCut':
        this.mods.rackSupply = 0.6;
        break;
      case 'surge':
        this.mods.requestRate = 2;
        break;
      case 'modelUpdate':
        this.mods.halluc = 0.15;
        break;
      case 'rackFailure': {
        // A named rack, or the one carrying the most work, goes down.
        const named = target ? this.buildings.get(this.tags.get(target) ?? -1) : undefined;
        const racks = named ? [named] : this.machines.filter((b) => b.kind === 'rack' && !b.corrupted);
        let best: Building | undefined;
        let most = -1;
        for (const r of racks) {
          let n = 0;
          for (const m of this.machines) if (BUILDINGS[m.kind].compute > 0 && Math.hypot(r.x + 1 - (m.x + m.w / 2), r.y + 1 - (m.y + m.h / 2)) <= TUNING.rackRadius) n++;
          if (n > most) {
            most = n;
            best = r;
          }
        }
        if (best) {
          const inc = this.newIncident('outage', 'hardware', best.x + 1, best.y + 1);
          this.corrupt(best, inc);
          this.topoDirty = true;
        }
        break;
      }
    }
  }

  // ---------------------------------------------------------------- plans

  /**
   * Build the current ghost plan for real in a fresh copy of this level and
   * run it. Deterministic, so the same plan always scores the same.
   */
  evaluatePlan(seconds: number): PlanResult {
    const lvl: LevelDef = {
      ...this.level,
      flags: { ...this.level.flags, planOnly: false },
      perturbations: [],
      timeLimit: 0,
      goal: { type: 'ship', count: 1e9 },
    };
    const sim = new World(lvl);
    sim.fog.set(this.fog);
    const ghosts = [...this.buildings.values()].filter((b) => b.ghost && BUILDINGS[b.kind].placeable).sort((p, q) => p.id - q.id);
    let cost = 0;
    for (const g of ghosts) cost += BUILDINGS[g.kind].cost;
    sim.credits = Math.max(sim.credits, cost);
    for (const g of ghosts) sim.apply({ type: 'place', kind: g.kind, x: g.x, y: g.y, dir: g.dir }, 'system');
    sim.credits = Math.max(0, this.level.credits - cost);
    const n = Math.round(seconds * TICK_RATE);
    for (let i = 0; i < n && sim.outcome === 'playing'; i++) sim.step();
    return {
      seconds, rate: sim.rate(), releases: sim.goodReleases, trust: Math.round(sim.trust), incidents: sim.incidents.length,
      lostUpdates: sim.lostUpdates, cost, budget: this.level.credits, overBudget: cost > this.level.credits, pieces: ghosts.length,
    };
  }

  private finishPlan() {
    if (this.outcome !== 'playing') return;
    const r = this.evaluatePlan(PLAN_SECONDS);
    this.planResult = r;
    const target = this.level.planTarget ?? 0;
    this.log({ type: 'planResult', ...r, target });
    // A plan passes if it hits the rate and users still trust it after three minutes.
    if (!r.overBudget && r.rate >= target && r.trust >= PLAN_TRUST) {
      this.outcome = 'won';
      this.outcomeReason = `Your plan ships ${r.rate} a minute in simulation, against ${target}, and users end at trust ${r.trust}.`;
      this.log({ type: 'won' });
    } else {
      this.outcome = 'lost';
      this.outcomeReason = r.overBudget
        ? `The plan costs ${r.cost}, over the ${r.budget} budget.`
        : r.rate < target
          ? `Your plan ships ${r.rate} a minute in simulation, short of ${target}.`
          : `Your plan hits ${r.rate} a minute, but users end at trust ${r.trust}.`;
      this.log({ type: 'lost', reason: 'plan' });
    }
  }

  /** End a run early from the menu, so the report still gets written. */
  forfeit() {
    if (this.outcome !== 'playing') return;
    this.outcome = 'lost';
    this.outcomeReason = 'You ended the run.';
    this.log({ type: 'lost', reason: 'quit' });
  }

  /** Stable fingerprint of world state, used by the determinism test. */
  hash(): string {
    let h = 2166136261;
    const mix = (n: number) => {
      h ^= Math.floor(n * 1000) | 0;
      h = Math.imul(h, 16777619);
    };
    mix(this.tick);
    mix(this.credits);
    mix(this.trust);
    mix(this.insight);
    mix(this.releases);
    mix(this.glitches.length);
    for (const b of this.buildings.values()) {
      mix(b.id);
      mix(b.x);
      mix(b.y);
      mix(b.corrupted);
      mix(b.slots.length);
      mix(b.inQ.length);
    }
    for (const g of this.glitches) {
      mix(g.x);
      mix(g.y);
    }
    return (h >>> 0).toString(16);
  }
}

/** Trust a plan must keep through its simulated run. */
export const PLAN_TRUST = 85;
/** How long a plan is simulated, for tests and for the final evaluation alike. */
export const PLAN_SECONDS = 180;

export function planSummary(r: PlanResult, target: number): string {
  if (!r.pieces) return 'Nothing planned yet.';
  const money = r.overBudget ? ` It costs ${r.cost}, over the ${r.budget} budget.` : ` Costs ${r.cost} of ${r.budget}.`;
  const issues = r.lostUpdates ? ` ${r.lostUpdates} lost updates.` : r.incidents ? ` ${r.incidents} incidents.` : '';
  return `In ${r.seconds}s of simulation this plan ships ${r.rate} a minute (target ${target}) and users end at trust ${r.trust} (needs ${PLAN_TRUST}).${issues}${money}`;
}
